import { BodyText } from '@/common/components/BodyText/BodyText';
import { BudgetIcon } from '@/common/components/BudgetIcon/BudgetIcon';
import { Card } from '@/common/components/Card/Card';
import { CircleProgress } from '@/common/components/CircleProgress/CircleProgress';
import { Heading } from '@/common/components/Heading/Heading';
import { Label } from '@/common/components/Label/Label';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { Stack } from '@/common/components/Stack/Stack';
import { ROUTES } from '@/common/constants/routes';
import { IEmployeeBudget } from '@/common/integrations/menhir/employee/budgets/budgets.types';
import {
  getIsBudgetAvailableSoon,
  getPercentageOfBudgetUsed,
  shouldTreatBudgetAsExpired,
  useGetBudgetCardDescription,
  useGetBudgetCardRecurrenceText,
  useGetBudgetCircleContent,
} from '@/common/utils/budgets';
import { useRouter } from 'expo-router';

export type TCardBudgetProps = {
  employeeBudget: IEmployeeBudget;
};

export function CardBudget({ employeeBudget }: TCardBudgetProps) {
  const router = useRouter();

  const isAvailableSoon = getIsBudgetAvailableSoon(employeeBudget);
  const isExpired = shouldTreatBudgetAsExpired(employeeBudget);
  const percentage = isAvailableSoon
    ? 100
    : getPercentageOfBudgetUsed(employeeBudget);

  const { title: circleTitle, description: circleDescription } =
    useGetBudgetCircleContent({
      employeeBudget,
      isAvailableSoon,
      isSpent: percentage === 100,
      isPayRoll: employeeBudget.budget.paymentMethod === 'payroll',
    });

  const description = useGetBudgetCardDescription(employeeBudget);
  const recurrenceText = useGetBudgetCardRecurrenceText({
    employeeBudget,
    isAvailableSoon,
  });

  return (
    <Card
      size="md"
      className="grow"
      onPress={() => {
        router.navigate({
          pathname: ROUTES.BUDGETS_DETAIL,
          params: { budgetId: employeeBudget.budget.id },
        });
      }}
    >
      <Stack horizontal gap="sm" align="center">
        <Stack gap="md" grow>
          <BudgetIcon
            type={employeeBudget.budget.logo}
            size={20}
            color={isExpired ? 'neutral20' : 'accentSecondary10'}
          />
          <Heading level={4}>{employeeBudget.budget.name}</Heading>
          <Stack>
            <BodyText color="neutral20" size="sm">
              {description}
            </BodyText>
            <BodyText color="neutral20" size="sm">
              {recurrenceText}
            </BodyText>
          </Stack>
        </Stack>
        {!isExpired && (
          <CircleProgress radius={50} strokeWidth={6} percentage={percentage}>
            <Label size="sm" bold>
              {circleTitle}
            </Label>
            <Label size="sm" color="neutral20">
              {circleDescription}
            </Label>
          </CircleProgress>
        )}
      </Stack>
    </Card>
  );
}

export function CardBudgetSkeleton() {
  return (
    <Card size="md" className="grow">
      <Stack horizontal gap="sm" align="center">
        <Stack gap="md" grow>
          <SkeletonInline className="h-[2rem] w-[2rem]" />
          <SkeletonInline className="h-[2.4rem] w-[12rem]" />
          <SkeletonInline className="h-[2rem] w-[12rem]" />
        </Stack>
        <SkeletonInline className="h-[10rem] w-[10rem] rounded-full" />
      </Stack>
    </Card>
  );
}
